/**
 * AJ Lead Capture — first-party state storage.
 *
 * LocalStorage primary, cookie fallback when unavailable. Values are stored
 * with an optional expiry. Session-scoped flags use sessionStorage.
 *
 * @module storage
 */

/**
 * Detect usable localStorage (private mode / blocked storage throws).
 *
 * @returns {boolean} Availability.
 */
function lsAvailable() {
  try {
    const k = '__aj_test__';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return true;
  } catch (e) {
    return false;
  }
}

const useLS = lsAvailable();

/**
 * Write a cookie.
 *
 * @param {string} name  Cookie name.
 * @param {string} value Cookie value.
 * @param {number} days  Lifetime in days (<=0 expires).
 * @returns {void}
 */
function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 86400000);
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax${secure}`;
}

/**
 * Read a cookie.
 *
 * @param {string} name Cookie name.
 * @returns {string|null} Value or null.
 */
function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Persist a value with an optional expiry (days).
 *
 * @param {string} key   Storage key.
 * @param {*}      value Serializable value.
 * @param {number} days  Days until expiry (0 = no expiry).
 * @returns {void}
 */
export function set(key, value, days = 0) {
  const expires = days > 0 ? Date.now() + days * 86400000 : 0;
  const record = JSON.stringify({ v: value, e: expires });
  if (useLS) {
    try {
      window.localStorage.setItem(key, record);
      return;
    } catch (e) { /* fall through to cookie */ }
  }
  setCookie(key, record, days > 0 ? days : 365);
}

/**
 * Read a value, honoring expiry.
 *
 * @param {string} key Storage key.
 * @returns {*} Stored value or null.
 */
export function get(key) {
  const raw = useLS ? window.localStorage.getItem(key) : getCookie(key);
  if (!raw) {
    return null;
  }
  try {
    const rec = JSON.parse(raw);
    if (rec.e && Date.now() > rec.e) {
      remove(key);
      return null;
    }
    return rec.v;
  } catch (e) {
    return null;
  }
}

/**
 * Remove a stored value.
 *
 * @param {string} key Storage key.
 * @returns {void}
 */
export function remove(key) {
  if (useLS) {
    try {
      window.localStorage.removeItem(key);
      return;
    } catch (e) { /* fall through */ }
  }
  setCookie(key, '', -1);
}

/**
 * Mark a session-scoped flag.
 *
 * @param {string} key Flag key.
 * @returns {void}
 */
export function sessionSet(key) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch (e) { /* ignore */ }
}

/**
 * Remove a session-scoped flag.
 *
 * @param {string} key Flag key.
 * @returns {void}
 */
export function sessionRemove(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) { /* ignore */ }
}

/**
 * Clear every key under a prefix from local + session storage and cookies.
 *
 * @param {string} prefix Key prefix, e.g. "aj_lo_".
 * @returns {void}
 */
export function clearNamespace(prefix) {
  ['localStorage', 'sessionStorage'].forEach((store) => {
    try {
      Object.keys(window[store])
        .filter((k) => k.indexOf(prefix) === 0)
        .forEach((k) => window[store].removeItem(k));
    } catch (e) { /* ignore */ }
  });
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (name.indexOf(prefix) === 0) {
      setCookie(name, '', -1);
    }
  });
}

/**
 * Read a session-scoped flag.
 *
 * @param {string} key Flag key.
 * @returns {boolean} Whether set.
 */
export function sessionHas(key) {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch (e) {
    return false;
  }
}
